<template>
  <div v-if="isEditing" class="w-80 max-w-full">
    <VideoPlayer v-if="path" :src="path" :aspect-height="9" :aspect-width="16"/>
    <FormSelectDropdown
      :model-value="prompt"
      @update:model-value="updatePrompt"
      :label="fieldName"
      :options="questions"
      placeholder="Select a prompt"
      class="mt-6 mb-4"
    />
    <div class="flex flex-wrap items-center justify-center gap-2">
      <FormButton v-if="path" @click="setConfirmRemoveRecording(true)" size="lg"
                  color="secondary" type="button">
        Delete
      </FormButton>
      <FormButton size="lg"
                  color="gradient"
                  type="button"
                  :class="!prompt && 'opacity-50 cursor-disabled'"
                  @click="setIsRecorderOpen(true)">
        {{ path && "Re-" }}Record
      </FormButton>
    </div>
    <IntroVideoWithRecorder
      v-model:show-prompt="showPrompt"
      :prompt="prompt"
      :video-prompt="videoPrompt"
      :path="path"
      v-if="isRecorderOpen"
      @close="setIsRecorderOpen(false)"
      @update:file="updateFile"
      @update:path="updatePath"
    />
    <FormButton color="green" @click="setIsEditing(false)" class="mx-auto mt-2 block max-w-fit" medium>
      Done
    </FormButton>
    <ModalConfirm v-model:open="showConfirmRemoveRecording" level="danger"
                  @confirm="deleteFile"
                  title="Are you sure you want to permanently delete your recording?"
                  description="Once it's deleted, there will be no way to recover it."
                  proceed-button-text="Delete"/>
  </div>
  <div v-else-if="path" class="w-80 max-w-full">
    <div v-if="path"
         class="flex h-44 w-80 max-w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-black"
         @click="play({path, prompt})">
      <PlayCircleIcon class="h-12 w-12 text-white"/>
    </div>
    <p v-if="prompt"
       @click="setIsEditing(true)"
       :class="editable && 'cursor-pointer hover:underline'"
       class="mt-2 text-sm font-medium text-slate-600 group-hover/candidate-item">
      {{ prompt }}
      <PencilSquareIcon v-if="editable" @click="setIsEditing(true)"
                        class="inline-block h-4 w-4 text-slate-600 opacity-50 group-hover/candidate-item:opacity-100 transition-all ml-1.5"/>
    </p>
    <PencilSquareIcon v-else-if="editable" @click="setIsEditing(true)"
                      class="block h-4 w-4 text-slate-600 opacity-50 transition-all ml-1.5 hover:opacity-100"/>
  </div>
  <FormButton v-else-if="editable" color="gradient" @click="setIsEditing(true)" flex
              class="block h-11">
    <PlusCircleIcon class="mr-1 h-4 w-4"/>
    <span>Add {{fieldName}}</span>
  </FormButton>
</template>

<script setup>
import FormButton from "./FormButton.vue";
import VideoPlayer from "./VideoPlayer.vue";
import IntroVideoWithRecorder from "./IntroVideoWithRecorder.vue";
import FormSelectDropdown from "./FormSelectDropdown.vue";
import ModalConfirm from "./ModalConfirm.vue";
import {ref, watch} from "vue";
import {PencilSquareIcon, PlusCircleIcon} from "@heroicons/vue/24/solid/index.js";
import {PlayCircleIcon} from "@heroicons/vue/24/outline/index.js";

const emits = defineEmits(["play", "update:path", "update:prompt", "update:file", "update:deleted"]);
const props = defineProps({
  editable: Boolean,
  path: String,
  prompt: String,
  fieldName: String,
  questions: Array,
  videoQuestions: Array,
});

const isEditing = ref(false);
const showPrompt = ref(false);
const videoPrompt = ref("");
const isRecorderOpen = ref(false);
const showConfirmRemoveRecording = ref(false);

const play = (src) => emits("play", src);

const setIsEditing = (value) => {
  if (!props.editable) return;
  isEditing.value = value;
};

const updatePrompt = (value) => {
  if (!props.editable) return;
  emits("update:prompt", value);
};

const updatePath = (value) => {
  if (!props.editable) return;
  emits("update:path", value);
};

const updateFile = (value) => {
  if (!props.editable) return;
  emits("update:file", value);
};

const updateDeleted = (value) => {
  if (!props.editable) return;
  emits("update:deleted", value);
};

const setIsRecorderOpen = (value) => {
  if (!props.editable) return;
  isRecorderOpen.value = value;
};

const setConfirmRemoveRecording = (value) => {
  if (!props.editable) return;
  showConfirmRemoveRecording.value = value;
};

const deleteFile = () => {
  updatePath("");
  updateFile(null);
  updateDeleted(true);
  showConfirmRemoveRecording.value = false;
};

watch(() => props.prompt, () => {
  if (!props.questions || !props.videoQuestions) return;

  const index = props.questions.findIndex((prompt) => prompt === props.prompt);
  if (index === -1) return;

  videoPrompt.value = props.videoQuestions[index];
  showPrompt.value = true;
}, {immediate: true});

defineExpose({
  isEditing,
  setIsEditing,
});
</script>
